import { useRef, useState, useEffect, useCallback } from "react";
import { Eraser, Pencil, Check, X, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { Overlay } from "@/components/flowly/infra/Overlay/Overlay";
import { IconButton } from "@/components/flowly/infra/IconButton/IconButton";
import { Button } from "@/components/flowly/infra/Button/Button";
import { Card } from "@/components/flowly/infra/Card/Card";
import { Slider } from "@/components/ui/slider";

interface MaskCanvasProps {
  imageUrl: string;
  onConfirm: (maskDataUrl: string, combinedDataUrl: string, blackWhiteMaskBlob: Blob) => void;
  onCancel: () => void;
}

type Tool = "brush" | "eraser";

/**
 * Canvas overlay for drawing masks on images for surgical editing
 * Opens as a centered modal with proper canvas sizing and focus trap
 */
export function MaskCanvas({ imageUrl, onConfirm, onCancel }: MaskCanvasProps) {
  const { t } = useLanguage();
  const modalRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageDisplayRef = useRef<HTMLImageElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [tool, setTool] = useState<Tool>("brush");
  const [brushSize, setBrushSize] = useState(18);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [displaySize, setDisplaySize] = useState({ width: 0, height: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [originalImageSize, setOriginalImageSize] = useState({ width: 0, height: 0 });
  const imageRef = useRef<HTMLImageElement | null>(null);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);

  // Load original image for export at full resolution
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      imageRef.current = img;
      setOriginalImageSize({ width: img.width, height: img.height });
    };
    img.src = imageUrl;
  }, [imageUrl]);

  // Focus trap - focus modal on mount
  useEffect(() => {
    modalRef.current?.focus();
  }, []);

  // Handle displayed image load - set canvas size to match displayed size
  const handleImageLoad = useCallback(() => {
    const displayedImg = imageDisplayRef.current;
    if (!displayedImg) return;

    // Use requestAnimationFrame to ensure accurate measurement after render
    requestAnimationFrame(() => {
      const displayedWidth = displayedImg.clientWidth;
      const displayedHeight = displayedImg.clientHeight;

      // Account for device pixel ratio for sharp drawing
      const dpr = window.devicePixelRatio || 1;

      setDisplaySize({ width: displayedWidth, height: displayedHeight });
      setCanvasSize({
        width: displayedWidth * dpr,
        height: displayedHeight * dpr,
      });

      setImageLoaded(true);

      // Scroll into view if needed
      containerRef.current?.scrollIntoView({ behavior: "auto", block: "center" });
    });
  }, []);

  // Initialize canvas with transparent background after size is set
  useEffect(() => {
    if (!imageLoaded || !canvasRef.current || canvasSize.width === 0) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Reset transform and scale for device pixel ratio
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    const dpr = window.devicePixelRatio || 1;
    ctx.scale(dpr, dpr);

    // Clear canvas (transparent)
    ctx.clearRect(0, 0, displaySize.width, displaySize.height);
  }, [imageLoaded, canvasSize, displaySize]);

  const getPosition = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      const canvas = canvasRef.current;
      if (!canvas) return { x: 0, y: 0 };

      const rect = canvas.getBoundingClientRect();

      if ("touches" in e) {
        const touch = e.touches[0];
        return {
          x: touch.clientX - rect.left,
          y: touch.clientY - rect.top,
        };
      }

      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    },
    []
  );

  const draw = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!isDrawing) return;

      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");
      if (!ctx || !canvas) return;

      const { x, y } = getPosition(e);

      ctx.globalCompositeOperation =
        tool === "eraser" ? "destination-out" : "source-over";
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.lineWidth = brushSize;

      if (tool === "brush") {
        ctx.strokeStyle = "rgba(34, 197, 94, 0.4)";
        ctx.fillStyle = "rgba(34, 197, 94, 0.4)";
      } else {
        ctx.strokeStyle = "rgba(0, 0, 0, 1)";
        ctx.fillStyle = "rgba(0, 0, 0, 1)";
      }

      // Draw line from last point for smooth strokes
      if (lastPointRef.current) {
        ctx.beginPath();
        ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
        ctx.lineTo(x, y);
        ctx.stroke();
      } else {
        ctx.beginPath();
        ctx.arc(x, y, brushSize / 2, 0, Math.PI * 2);
        ctx.fill();
      }

      lastPointRef.current = { x, y };
    },
    [isDrawing, tool, brushSize, getPosition]
  );

  const startDrawing = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDrawing(true);
      lastPointRef.current = null;

      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");
      if (!ctx || !canvas) return;

      const { x, y } = getPosition(e);

      ctx.globalCompositeOperation =
        tool === "eraser" ? "destination-out" : "source-over";

      if (tool === "brush") {
        ctx.fillStyle = "rgba(34, 197, 94, 0.4)";
      } else {
        ctx.fillStyle = "rgba(0, 0, 0, 1)";
      }

      ctx.beginPath();
      ctx.arc(x, y, brushSize / 2, 0, Math.PI * 2);
      ctx.fill();

      lastPointRef.current = { x, y };
    },
    [tool, brushSize, getPosition]
  );

  const stopDrawing = useCallback(() => {
    setIsDrawing(false);
    lastPointRef.current = null;
  }, []);

  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx || !canvas) return;

    ctx.clearRect(0, 0, displaySize.width, displaySize.height);
  }, [displaySize]);

  const handleConfirm = useCallback(async () => {
    const canvas = canvasRef.current;
    const originalImg = imageRef.current;
    if (!canvas || !originalImg) return;

    const w = originalImg.width;
    const h = originalImg.height;

    // Create black/white mask for inpainting API
    const bwMaskCanvas = document.createElement("canvas");
    bwMaskCanvas.width = w;
    bwMaskCanvas.height = h;
    const bwCtx = bwMaskCanvas.getContext("2d");
    if (!bwCtx) return;

    // Fill with black background (areas to keep)
    bwCtx.fillStyle = "black";
    bwCtx.fillRect(0, 0, w, h);

    // Draw the user's drawing scaled to original dimensions
    bwCtx.drawImage(canvas, 0, 0, w, h);

    // Convert painted areas to white (areas to edit)
    const imgData = bwCtx.getImageData(0, 0, w, h);
    const data = imgData.data;

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];
      // Check if pixel was painted (alpha > 10 and has some color)
      const painted = a > 10 && (r + g + b) > 0;
      if (painted) {
        // Set to white (area to edit)
        data[i] = 255; data[i + 1] = 255; data[i + 2] = 255; data[i + 3] = 255;
      } else {
        // Set to black (area to keep)
        data[i] = 0; data[i + 1] = 0; data[i + 2] = 0; data[i + 3] = 255;
      }
    }

    bwCtx.putImageData(imgData, 0, 0);

    // Export green overlay mask for preview
    const greenMaskCanvas = document.createElement("canvas");
    greenMaskCanvas.width = w;
    greenMaskCanvas.height = h;
    const greenCtx = greenMaskCanvas.getContext("2d");
    if (!greenCtx) return;
    greenCtx.drawImage(canvas, 0, 0, w, h);
    const maskDataUrl = greenMaskCanvas.toDataURL("image/png");

    // Create combined preview (original + overlay)
    const combinedCanvas = document.createElement("canvas");
    combinedCanvas.width = w;
    combinedCanvas.height = h;
    const combinedCtx = combinedCanvas.getContext("2d");
    if (!combinedCtx) return;
    combinedCtx.drawImage(originalImg, 0, 0);
    combinedCtx.drawImage(canvas, 0, 0, w, h);
    const combinedDataUrl = combinedCanvas.toDataURL("image/png");

    // Create black/white mask blob for API
    const bwMaskBlob = await new Promise<Blob>((resolve, reject) => {
      bwMaskCanvas.toBlob(
        (blob) => blob ? resolve(blob) : reject(new Error("Mask blob creation failed")),
        "image/png"
      );
    });

    onConfirm(maskDataUrl, combinedDataUrl, bwMaskBlob);
  }, [onConfirm]);

  return (
    <Overlay isOpen={true} onClose={onCancel} className="bg-black/[0.92]">
      {/* Modal Content */}
      <div
        ref={modalRef}
        tabIndex={-1}
        className="relative flex flex-col items-center justify-center w-full h-full max-w-[92vw] max-h-[86vh] outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between w-full max-w-4xl mb-4">
          <h3 className="text-white font-bold text-lg">{t.maskCanvas.title}</h3>
          <IconButton
            variant="overlay"
            onClick={onCancel}
            className="rounded-xl"
          >
            <X className="w-6 h-6" />
          </IconButton>
        </div>

        {/* Canvas container - centered with proper constraints */}
        <div
          ref={containerRef}
          className="flex items-center justify-center flex-1 w-full"
        >
          {/* Wrapper: sized by image, canvas overlays via absolute inset-0 */}
          <div className="relative">
            {/* Background image - determines wrapper size */}
            <img
              ref={imageDisplayRef}
              src={imageUrl}
              alt="Original"
              className="rounded-2xl select-none"
              style={{
                maxWidth: "92vw",
                maxHeight: "70vh",
                width: "auto",
                height: "auto",
                objectFit: "contain",
                pointerEvents: "none",
              }}
              onLoad={handleImageLoad}
              draggable={false}
            />

            {/* Drawing canvas overlay - exact same size as displayed image */}
            {imageLoaded && displaySize.width > 0 && (
              <canvas
                ref={canvasRef}
                width={canvasSize.width}
                height={canvasSize.height}
                className="absolute inset-0 rounded-2xl touch-none"
                style={{
                  width: displaySize.width,
                  height: displaySize.height,
                  cursor: tool === "brush" ? "crosshair" : "cell",
                  pointerEvents: "auto",
                }}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
            )}
          </div>
        </div>

        {/* Toolbar */}
        <Card className="flex flex-wrap items-center justify-center gap-3 mt-6 bg-white/10 border-white/10 p-3 backdrop-blur-sm">
          {/* Tool selection */}
          <div className="flex items-center gap-2">
            <IconButton
              variant="overlay"
              onClick={() => setTool("brush")}
              tooltip={t.maskCanvas.brush}
              tooltipSide="bottom"
              className={cn(
                "rounded-xl",
                tool === "brush"
                  ? "bg-green-500 text-white hover:bg-green-600"
                  : "bg-white/10 text-white/60 hover:bg-white/20 hover:text-white"
              )}
            >
              <Pencil className="w-5 h-5" />
            </IconButton>
            <IconButton
              variant="overlay"
              onClick={() => setTool("eraser")}
              tooltip={t.maskCanvas.eraser}
              tooltipSide="bottom"
              className={cn(
                "rounded-xl",
                tool === "eraser"
                  ? "bg-red-500 text-white hover:bg-red-600"
                  : "bg-white/10 text-white/60 hover:bg-white/20 hover:text-white"
              )}
            >
              <Eraser className="w-5 h-5" />
            </IconButton>
          </div>

          {/* Brush size */}
          <div className="flex items-center gap-2 px-3">
            <span className="text-white/60 text-xs">{t.maskCanvas.brushSize}</span>
            <Slider
              min={5}
              max={50}
              step={1}
              value={[brushSize]}
              onValueChange={([val]) => setBrushSize(val)}
              className="w-20 sm:w-24 [&_[data-slot=slider-track]]:bg-white/20 [&_[data-slot=slider-range]]:bg-primary [&_[data-slot=slider-thumb]]:border-primary"
            />
            <span className="text-white text-xs w-6">{brushSize}</span>
          </div>

          {/* Clear button */}
          <IconButton
            variant="overlay"
            onClick={clearCanvas}
            tooltip={t.maskCanvas.clear}
            tooltipSide="bottom"
            className="rounded-xl bg-white/10 text-white/60 hover:bg-white/20 hover:text-white"
          >
            <RotateCcw className="w-5 h-5" />
          </IconButton>

          {/* Confirm button */}
          <Button
            variant="primary"
            size="sm"
            rounded="xl"
            onClick={handleConfirm}
            className="font-bold"
          >
            <Check className="w-5 h-5" />
            {t.maskCanvas.confirm}
          </Button>
        </Card>

        {/* Instructions */}
        <p className="text-white/50 text-sm mt-4 text-center max-w-md px-4">
          {t.maskCanvas.instructions}
        </p>
      </div>
    </Overlay>
  );
}
